/**
 * Hooks React Query pra leitura no painel superadmin de clínicas.
 *
 * Escopo: lista, detalhe, members, subscription ativa e timeline de
 * provisionamento. Mutations (resend welcome email, suspender, etc)
 * são adicionadas em PRs futuras conforme necessidade — esta PR foca
 * em leitura pra reabilitar o botão "Acessar painel da clínica" do
 * `LeadCustomerBanner`.
 *
 * RLS: superadmin tem policies permissivas em `clinics`,
 * `clinic_members`, `subscriptions` e `clinic_provisioning_events`
 * (migrations 0021/0035). Reads filtram `deleted_at IS NULL` quando
 * a tabela tem soft-delete.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { supabase } from '../../lib/supabase.js';

import {
  type Clinic,
  type ClinicMember,
  type PlanLite,
  type ProvisioningEvent,
  type Subscription,
} from './schemas.js';

export const CLINIC_KEYS = {
  list: () => ['clinics', 'list'] as const,
  detail: (id: string) => ['clinics', 'detail', id] as const,
  members: (clinicId: string) => ['clinics', 'members', clinicId] as const,
  subscription: (clinicId: string) => ['clinics', 'subscription', clinicId] as const,
  provisioningEvents: (clinicId: string) => ['clinics', 'provisioningEvents', clinicId] as const,
};

/**
 * Lista todas as clínicas ativas (`deleted_at IS NULL`). Ordenada por
 * `created_at` descendente — clínicas recém-provisionadas aparecem
 * primeiro. Usado pelo `ClinicsListPage`.
 */
export function useClinics() {
  return useQuery({
    queryKey: CLINIC_KEYS.list(),
    staleTime: 30_000,
    queryFn: async (): Promise<Clinic[]> => {
      const { data, error } = await supabase
        .from('clinics')
        .select('*')
        .is('deleted_at', null)
        .order('created_at', { ascending: false });
      if (error) throw new Error(error.message);
      return (data ?? []) as Clinic[];
    },
  });
}

/** Detalhe de uma clínica pelo id. Null quando não acha (usado pra
 * 404 na página). */
export function useClinic(id: string | undefined) {
  return useQuery({
    queryKey: CLINIC_KEYS.detail(id ?? ''),
    enabled: Boolean(id),
    staleTime: 15_000,
    queryFn: async (): Promise<Clinic | null> => {
      // Guard redundante com `enabled: Boolean(id)` mas necessário pra
      // narrowing TS em modo build (`tsc -b` strict). Em runtime,
      // queryFn nunca roda com `id` undefined porque `enabled` é false.
      if (!id) return null;
      const { data, error } = await supabase
        .from('clinics')
        .select('*')
        .eq('id', id)
        .is('deleted_at', null)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return (data as Clinic | null) ?? null;
    },
  });
}

/** Lista members de uma clínica. Inclui inativos pra audit ("usuário X
 * foi removido") — UI filtra se quiser só ativos. */
export function useClinicMembers(clinicId: string | undefined) {
  return useQuery({
    queryKey: CLINIC_KEYS.members(clinicId ?? ''),
    enabled: Boolean(clinicId),
    staleTime: 15_000,
    queryFn: async (): Promise<ClinicMember[]> => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from('clinic_members')
        .select('*')
        .eq('clinic_id', clinicId)
        .is('deleted_at', null)
        .order('created_at', { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []) as ClinicMember[];
    },
  });
}

/**
 * Subscription ativa da clínica + plano embedado. Usa
 * `subscriptions.deleted_at IS NULL` + `clinic_id`. Em prod esperamos
 * exatamente 1 ativa, mas tomamos `order DESC` + `limit 1` defensivo.
 */
export function useClinicSubscription(clinicId: string | undefined) {
  return useQuery({
    queryKey: CLINIC_KEYS.subscription(clinicId ?? ''),
    enabled: Boolean(clinicId),
    staleTime: 15_000,
    queryFn: async (): Promise<{ subscription: Subscription; plan: PlanLite | null } | null> => {
      if (!clinicId) return null;
      // Supabase JS embed nested vira array TS-ambíguo; pegamos plano
      // separadamente pra evitar narrowing complicado.
      const subQ = await supabase
        .from('subscriptions')
        .select('*')
        .eq('clinic_id', clinicId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(1);
      if (subQ.error) throw new Error(subQ.error.message);
      const sub = (subQ.data ?? [])[0] as Subscription | undefined;
      if (!sub) return null;

      const planQ = await supabase
        .from('plans')
        .select('id, key, name_pt_br, tier, monthly_price_brl, yearly_price_brl, billing_cycle')
        .eq('id', sub.plan_id)
        .maybeSingle();
      if (planQ.error) throw new Error(planQ.error.message);
      return {
        subscription: sub,
        plan: (planQ.data as PlanLite | null) ?? null,
      };
    },
  });
}

/**
 * Timeline de eventos de provisionamento. Append-only, ordenado por
 * `created_at ASC` pra mostrar evolução cronológica. Cada `step` aparece
 * potencialmente múltiplas vezes (retry após falha) — UI mostra todos.
 */
export function useClinicProvisioningEvents(clinicId: string | undefined) {
  return useQuery({
    queryKey: CLINIC_KEYS.provisioningEvents(clinicId ?? ''),
    enabled: Boolean(clinicId),
    staleTime: 30_000,
    queryFn: async (): Promise<ProvisioningEvent[]> => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from('clinic_provisioning_events')
        .select('*')
        .eq('clinic_id', clinicId)
        .order('created_at', { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []) as ProvisioningEvent[];
    },
  });
}

/**
 * Reenvia o magic link de invite pro owner da clínica. Usado quando o
 * link original expirou (TTL Supabase 1h), email caiu em spam, ou
 * Karla precisa disparar manualmente.
 *
 * Edge function: `resend-owner-invite` (verify_jwt: true, requer JWT
 * do superadmin). Idempotente do ponto de vista do banco — múltiplas
 * chamadas só geram emails extras (Daniela aceita o mais recente).
 *
 * Não faz nada se member já está `'active'` — retorna `action='already_accepted'`.
 */
export interface ResendInviteResult {
  status: string;
  action?: 'resent' | 'already_accepted';
  email_status?: 'sent' | 'failed';
  member_status?: string;
}

export function useResendOwnerInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { clinic_id: string }): Promise<ResendInviteResult> => {
      const { data, error } = await supabase.functions.invoke('resend-owner-invite', {
        body: { clinic_id: input.clinic_id },
      });
      if (error) throw new Error(error.message);
      return data as ResendInviteResult;
    },
    onSuccess: (_data, variables) => {
      // Invalida members + provisioningEvents da clinic — pode haver
      // novo activity ou state change.
      qc.invalidateQueries({ queryKey: CLINIC_KEYS.members(variables.clinic_id) });
      qc.invalidateQueries({
        queryKey: CLINIC_KEYS.provisioningEvents(variables.clinic_id),
      });
    },
  });
}
